import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser } from '../services/authService';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import styles from '../styles/admin/loginStyles';
import TurnstileWidget from '../components/common/TurnstileWidget';
import config from '../config';


const Register = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        name: '',
        orcid: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const turnstileRef = useRef(null);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const validateForm = () => {
        if (!formData.name || !formData.email || !formData.password) {
            setError('Name, email, and password are required');
            return false;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters long');
            return false;
        }

        const emailRegex = /^\S+@\S+\.\S+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            return false;
        }

        if (!agreedToTerms) {
            setError('You must agree to the Terms and Conditions and Privacy Policy');
            return false;
        }

        if (!captchaToken) {
            setError('Please complete the verification step.');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setError('');
        setIsLoading(true);

        try {
            const { confirmPassword, ...registrationData } = formData;
            await registerUser({ ...registrationData, captchaToken });

            // Redirect to verification pending page with email
            navigate('/verification-pending', { state: { email: formData.email } });
        } catch (err) {
            setError(err.message || 'Registration failed. Please try again.');
            // Turnstile tokens are single-use - always get a fresh one before the next attempt,
            // regardless of why this attempt failed.
            turnstileRef.current?.reset();
            setCaptchaToken('');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.wrapper}>
            <div style={{ ...styles.form, maxWidth: '500px' }}>
                <h2 style={styles.heading}>Register</h2>
                <form onSubmit={handleSubmit}>
                    <div style={styles.inputGroup}>
                        <input
                            type="text"
                            name="name"
                            placeholder="Full Name *"
                            value={formData.name}
                            onChange={handleChange}
                            autoFocus
                            style={styles.inputField}
                        />
                    </div>

                    <div style={styles.inputGroup}>
                        <input
                            type="email"
                            name="email"
                            placeholder="Email *"
                            value={formData.email}
                            onChange={handleChange}
                            style={styles.inputField}
                        />
                    </div>

                    <div style={styles.inputGroup}>
                        <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            placeholder="Password *"
                            value={formData.password}
                            onChange={handleChange}
                            style={styles.inputField}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={styles.passwordToggleIcon}
                            tabIndex="-1"
                        >
                            {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                        </button>
                    </div>

                    <div style={styles.inputGroup}>
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="confirmPassword"
                            placeholder="Confirm Password *"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            style={styles.inputField}
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            style={styles.passwordToggleIcon}
                            tabIndex="-1"
                        >
                            {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                        </button>
                    </div>



                    <div style={styles.inputGroup}>
                        <input
                            type="text"
                            name="orcid"
                            placeholder="ORCID (optional)"
                            value={formData.orcid}
                            onChange={handleChange}
                            style={styles.inputField}
                        />
                    </div>



                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '20px',
                        justifyContent: 'flex-start',
                        textAlign: 'left'
                    }}>
                        <input
                            type="checkbox"
                            id="agreedToTerms"
                            checked={agreedToTerms}
                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                            style={{
                                marginRight: '10px',
                                cursor: 'pointer',
                                width: '18px',
                                height: '18px',
                                accentColor: '#2563eb'
                            }}
                        />
                        <label
                            htmlFor="agreedToTerms"
                            style={{
                                fontSize: '14px',
                                color: '#4a5568',
                                cursor: 'pointer',
                                userSelect: 'none'
                            }}
                        >
                            I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>Terms and Conditions</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>Privacy Policy</a>
                        </label>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <TurnstileWidget
                            ref={turnstileRef}
                            siteKey={config.turnstileSiteKey}
                            onVerify={(token) => {
                                setCaptchaToken(token);
                                setError('');
                            }}
                            onExpire={() => setCaptchaToken('')}
                            onError={() => setCaptchaToken('')}
                        />
                    </div>

                    {error && <p style={styles.errorMessage}>{error}</p>}

                    <button
                        type="submit"
                        style={{
                            ...styles.submitBtn,
                            backgroundColor: isLoading ? '#cccccc' : styles.submitBtn.backgroundColor,
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Registering...' : 'Register'}
                    </button>

                    <button
                        type="button"
                        style={styles.homeBtn}
                        onMouseOver={(e) => (e.target.style.backgroundColor = '#5a6268')}
                        onMouseOut={(e) => (e.target.style.backgroundColor = '#6c757d')}
                        onClick={() => navigate('/login')}
                    >
                        Already have an account? Login
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Register;
